import React, {useEffect, useState} from "react";
import PropTypes from 'prop-types';
import {makeStyles, TextField} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import Autocomplete from "@material-ui/lab/Autocomplete";
import JsonScreenHoc from "../../../../../../HocHelpers/JsonScreenHoc";
import findByString from "../../../../../../Utils/Form/FormDynamic/Utils/getValueByString";
import {useSelector} from "react-redux";
import {multiLanguageValue} from "../../../../Utils/lib";
import FieldScreenComponent from "../../FieldScreenComponent";
import {getParamsFromString} from "../../../../Utils";
import {executeJsString} from "../../../../Utils/getParamsFromString";
import SelectType from "../../../../../../Utils/Form/FormDynamic/Fields/SelectType/Select";
import DefaultType from "../../../../../../Utils/Form/FormDynamic/Fields/DefaultType/DefaultType";

const useStyles = makeStyles(() => ({
    questionWrapper: {
        display: 'flex',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
    },
    questionLabel: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        padding: '0 5px',
        borderRight: '1px solid rgba(0, 0, 0, 0.12)'
    },
    questionValue: {
        flex: 1,
        padding: '0 5px',
        borderRight: '1px solid rgba(0, 0, 0, 0.12)'
    },
    questionWeight: {
        width: 40,
        padding: '0 5px',
    },
    option: {
        minHeight: 2,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        cursor: 'pointer',
        paddingTop: 1,
        boxSizing: 'border-box',
        outline: '0',
        WebkitTapHighlightColor: 'transparent',
        paddingBottom: 2,
        paddingLeft: 16,
        paddingRight: 16,
    },

}));


const Questions = ({questions, setValue, fieldName, globalValues, styles, loading, screenName}) => {
    return (
        loading ? (
            <Skeleton animation={"wave"}/>
        ) : (
            questions.map((question, index) => (
                <React.Fragment key={index}>
                    <QuestionItem
                        question={question}
                        setValue={setValue}
                        globalValues={globalValues}
                        fieldName={fieldName}
                        index={index}
                        styles={styles}
                        screenName={screenName}
                    />
                </React.Fragment>
            ))
        )
    )
};
export default JsonScreenHoc(Questions)
Questions.propTypes = {
    questions: PropTypes.array,
    setValue: PropTypes.func,
    fieldName: PropTypes.string
};
Questions.defaultProps = {
    questions: [],
    fieldName: ""
};


const QuestionItem = (props) => {
    const {question, setValue, fieldName, globalValues, index, styles, screenName} = props;
    const classes = useStyles();
    const userLanguage = useSelector(state => state.app.userLanguage);
    return (
        <div className={classes.questionWrapper} style={{background: styles.background, textAlign: styles.textAlign}}>
            <div className={classes.questionLabel}
                 style={{
                     color: styles.color,
                     fontSize: styles.fontSize,
                     fontWeight: styles.fontWeight,
                     fontStyle: styles.fontStyle,
                     padding: styles.padding
                 }}
            >
                {multiLanguageValue(question.label, userLanguage)}
            </div>
            <div className={classes.questionValue}>
                <QuestionValue
                    question={question}
                    setValue={setValue}
                    fieldName={fieldName}
                    globalValues={globalValues}
                    index={index}
                    styles={styles}
                    screenName={screenName}
                />
            </div>
        </div>
    )
};
QuestionItem.propTypes = {
    question: PropTypes.shape({
        type: PropTypes.oneOf(['field', 'choice']),
        name: PropTypes.string,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        field: PropTypes.object,
        choices: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.string,
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
            points: PropTypes.number,
        })),
        weight: PropTypes.number,
        answerValue: PropTypes.shape({
            entity: PropTypes.string,
            field: PropTypes.string,
            script: PropTypes.string,
        }),
        pointsScript: PropTypes.string,
    })
}

const QuestionValue = (props) => {
    const {
        setValue,
        fieldName,
        globalValues,
        index,
        styles,
        screenName,
        question
    } = props;
    const {type, weight, choices, name, field, pointsScript} = question;
    const classes = useStyles();

    const [options, setOptions] = useState([]);
    const [currentValue, setCurrentValue] = useState({value: "", label: ""});
    const userLanguage = useSelector(state => state.app.userLanguage);

    useEffect(() => {
        if (type === 'choice') {
            const value = findByString(globalValues.param[screenName], `${fieldName}.answers.${index}`);

            setOptions((choices || []).map(ch => {
                return {...ch, label: multiLanguageValue(ch.label, userLanguage), value: ch.id}
            }));
            setCurrentValue(value);
        }
    }, [globalValues, choices, question.type]);

    function onChangeFields(value, p) {

        function calculatePoints(script, doc) {
            const points = executeJsString(script, {doc})
            return points || 0;
        }

        const questionPoints = calculatePoints(pointsScript, {value})
        setValue({
            name, value, weight, points: questionPoints
        }, `${fieldName}.answers.${index}`)
    }

    if (question.type === 'choice') {
        const onChange = (value) => {
            const {points} = options.find(option => option.value === value);
            setValue({
                name,
                points,
                weight,
                value,
            }, `${fieldName}.answers.${index}`)
        };

        return (
            <>
                <SelectType
                    field={{
                        type: 'objSelect',
                        values: options,
                        path: `${fieldName}.answers.${index}.value`
                    }}
                    fieldStyles={{
                        withoutStyles: true,
                        fullWidth: true,
                    }}
                    setValue={onChange}
                    values={globalValues.param[screenName]}
                />
            </>
        )
    }

    return (
        <>
            <FieldScreenComponent
                field={{
                    properties: {
                        ...field,
                        path: `${fieldName}.answers.${index}.value`,
                    },
                    styles: [{fullWidth: true, withoutStyles: true}],
                }}
                setValue={onChangeFields}
            />
        </>
    )
};

QuestionValue.propTypes = {
    question: PropTypes.shape({
        type: PropTypes.oneOf(['field', 'choice']),
        name: PropTypes.string,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        field: PropTypes.object,
        choices: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.string,
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
            points: PropTypes.number,
        })),
        weight: PropTypes.number,
        answerValue: PropTypes.shape({
            entity: PropTypes.string,
            field: PropTypes.string,
            script: PropTypes.string,
        }),
        pointsScript: PropTypes.string,
    })
}